import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import type { ReactNode } from "react";

type Language = "ko" | "en" | "zh" | "ja";

const LANG_LABELS: Record<Language, string> = {
  ko: "🇰🇷 KO",
  en: "🇺🇸 EN",
  zh: "🇨🇳 ZH",
  ja: "🇯🇵 JA",
};

type Props = {
  open: boolean;
  onClose: () => void;
  /** URL to load inside the iframe. If provided, renders an iframe. */
  src?: string;
  /** Custom content to render inside the phone frame. */
  children?: ReactNode;
  title?: string;
  defaultLang?: Language;
  onLangChange?: (lang: Language) => void;
};

/**
 * 모바일 미리보기 모달.
 * 실제 모바일 비율(390×844, iPhone 14 Pro) 프레임으로 URL 또는 커스텀 콘텐츠를 표시.
 * 언어 전환 토글 포함 — 상위에서 onLangChange로 iframe src를 업데이트.
 */
export function MobilePreviewModal({
  open,
  onClose,
  src,
  children,
  title = "미리보기",
  defaultLang = "ko",
  onLangChange,
}: Props) {
  const [lang, setLang] = useState<Language>(defaultLang);
  const [langDropdown, setLangDropdown] = useState(false);

  function switchLang(l: Language) {
    setLang(l);
    setLangDropdown(false);
    onLangChange?.(l);
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal container */}
          <motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", stiffness: 350, damping: 30 }}
            className="fixed left-1/2 top-1/2 z-50 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-4"
          >
            {/* Header: title + lang switcher + close */}
            <div className="flex w-full items-center justify-between">
              <span className="text-sm font-semibold text-white">{title}</span>
              <div className="flex items-center gap-2">
                {/* Language switcher */}
                <div className="relative">
                  <button
                    onClick={() => setLangDropdown((v) => !v)}
                    className="flex items-center gap-1 rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm hover:bg-white/25"
                  >
                    {LANG_LABELS[lang]}
                    <span className="text-white/60">{langDropdown ? "▲" : "▼"}</span>
                  </button>
                  <AnimatePresence>
                    {langDropdown && (
                      <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.95 }}
                        className="absolute right-0 top-full z-10 mt-1 w-28 overflow-hidden rounded-xl bg-white shadow-xl"
                      >
                        {(Object.keys(LANG_LABELS) as Language[]).map((l) => (
                          <button
                            key={l}
                            onClick={() => switchLang(l)}
                            className={`flex w-full items-center gap-2 px-3 py-2 text-xs transition-colors hover:bg-gray-50 ${
                              l === lang ? "bg-gray-50 font-semibold" : "text-gray-700"
                            }`}
                          >
                            {LANG_LABELS[l]}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <button
                  onClick={onClose}
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Phone frame */}
            <div
              className="relative overflow-hidden rounded-[44px] bg-black shadow-2xl ring-4 ring-white/20"
              style={{ width: 390, height: 844 }}
            >
              {/* Notch */}
              <div className="absolute left-1/2 top-3 z-10 h-7 w-28 -translate-x-1/2 rounded-full bg-black" />

              {/* Screen */}
              <div className="absolute inset-0 overflow-hidden rounded-[44px] bg-white">
                {src ? (
                  <iframe
                    src={`${src}?lang=${lang}&preview=1`}
                    className="h-full w-full border-0"
                    title="모바일 미리보기"
                  />
                ) : (
                  <div className="h-full w-full overflow-auto">{children}</div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
